import { Injectable } from '@angular/core';
import {
  LabService,
  PlayOptionItem, VideoSceneFilterRequest,
  VideoSceneResponse,
  VideoScenesService,
  VideoSceneUpdateRequest
} from '@solverml/api';
import { VideoStreamingService } from './video-streaming.service';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { HttpResponse } from "@angular/common/http";

export type PlayModelOptions = Record<string, PlayOptionItem[]>;
const PLAY_OPTIONS_CACHE_KEY = 'playModelOptions';
const PLAY_OPTIONS_CACHE_TIMESTAMP_KEY = 'playModelOptionsTimestamp';
const CACHE_DURATION_MS = 30 * 60 * 1000;

@Injectable({
  providedIn: 'root'
})

export class VideoSceneService {

  constructor(
    private videoScenesService: VideoScenesService,
    private labService: LabService
  ) { }

  async getScenes(videoId: string, twelveSeconds: boolean = false): Promise<VideoSceneResponse[]> {
    if (!videoId) return [];

    const endpointResults: VideoSceneResponse[] = await lastValueFrom(
      this.videoScenesService.getAllScenes(videoId, twelveSeconds)
    );

    return endpointResults.sort((a, b) => a.start_time_in_seconds - b.start_time_in_seconds);
  }

  async getFilteredScenes(
    videoId: string,
    playDefinitionIds: number[],
    attackingTeamId?: string | null,
    playModelQuery?: string | null,
    twelveSeconds: boolean = false
  ): Promise<VideoSceneResponse[]> {

    if (!videoId) return [];

    // Si no hay filtros, llama al endpoint normal 'getScenes' para
    if ((!playDefinitionIds || playDefinitionIds.length === 0) && !attackingTeamId && !playModelQuery) {
      return this.getScenes(videoId, twelveSeconds);
    }

    const filterPayload: any = {
      play_definition_ids: playDefinitionIds || [],
      attacking_team_id: attackingTeamId || null,
      play_model_query: playModelQuery || null,
      twelve_seconds: twelveSeconds
    };

    const endpointResults: VideoSceneResponse[] = await lastValueFrom(
      this.videoScenesService.filterScenesByPlayDefinitionOrTeam(videoId, filterPayload)
    );
    return endpointResults.sort((a, b) => a.start_time_in_seconds - b.start_time_in_seconds);
  }


  async getPlayModelOptions(): Promise<PlayModelOptions> {
    const cachedTimestamp = localStorage.getItem(PLAY_OPTIONS_CACHE_TIMESTAMP_KEY);
    const cachedData = localStorage.getItem(PLAY_OPTIONS_CACHE_KEY);

    if (cachedData && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp, 10);
      const isCacheValid = Date.now() - timestamp < CACHE_DURATION_MS;
      if (isCacheValid) {
        return JSON.parse(cachedData) as PlayModelOptions;
      }
    }

    // Obtener desde la API
    try {

      const options = await firstValueFrom(
        this.labService.getPlayModelOptions()
      ) as PlayModelOptions;

      // Guarda la estructura en caché
      localStorage.setItem(PLAY_OPTIONS_CACHE_KEY, JSON.stringify(options));
      localStorage.setItem(PLAY_OPTIONS_CACHE_TIMESTAMP_KEY, Date.now().toString());

      return options;
    } catch (error) {
      console.error('Error al obtener las opciones del modelo de jugadas:', error);
      return {};
    }
  }

  /**
   * Llama al endpoint de exportación y devuelve la respuesta HTTP completa.
   * Sigue el patrón async/await usado en otras partes de la aplicación.
   * @param videoId El ID del vídeo a exportar.
   * @param twelveSeconds Parámetro para forzar duración 12s.
   * @returns Una Promesa que resuelve a un HttpResponse<Blob>.
   */
  public async exportScenesToCsv(videoId: string, twelveSeconds: boolean = false): Promise<HttpResponse<Blob>> {
    return await lastValueFrom(
      this.videoScenesService.exportVideoScenes(
        videoId,
        twelveSeconds,
        'response',
        false,
        { httpHeaderAccept: 'text/csv' } as any
      )
    );
  }

  /**
   * @param sceneId El ID de la jugada que se va a eliminar.
   * @param videoId El ID del vídeo al que pertenece la jugada.
   * @returns Una promesa que resuelve con el objeto de la jugada eliminada.
   */
  public async deleteScene(sceneId: string, videoId: string): Promise<VideoSceneResponse> {
    return await lastValueFrom(
      this.videoScenesService.deleteScene(sceneId, videoId)
    );
  }

  /**
   * @param scene El objeto VideoSceneDTO con los datos actualizados.
   * @returns Una promesa que resuelve con la jugada actualizada desde el backend.
   */
  public async updateScene(scene: VideoSceneUpdateRequest): Promise<VideoSceneResponse> {
    if (!scene.id) {
      throw new Error('Scene ID is required for an update.');
    }

    return await lastValueFrom(
      this.videoScenesService.updateScene(scene.id, scene)
    );
  }

}
